// src/components/system/utils/configManager.js
// Configuration Management Utilities
// Created: 2025-01-20 16:47:00 UTC+8
// Purpose: Utilities for loading, saving, validating, and managing website configurations

import { defaultWebsiteConfig, configValidationRules } from '../config/websiteConfig.js';

/**
 * Configuration Manager Class
 * Handles all configuration operations including validation, loading, and saving
 */
export class ConfigManager {
  constructor() {
    this.config = { ...defaultWebsiteConfig };
    this.listeners = [];
  }

  /**
   * Load configuration from localStorage or use default
   * @param {string} configKey - Key to store/retrieve config from localStorage
   * @returns {Object} - Loaded configuration
   */
  loadConfig(configKey = 'websiteConfig') {
    try {
      const storedConfig = localStorage.getItem(configKey);
      if (storedConfig) {
        const parsedConfig = JSON.parse(storedConfig);
        this.config = this.mergeConfigs(defaultWebsiteConfig, parsedConfig);
      } else {
        this.config = { ...defaultWebsiteConfig };
      }
      this.notifyListeners('configLoaded', this.config);
      return this.config;
    } catch (error) {
      console.error('Error loading configuration:', error);
      this.config = { ...defaultWebsiteConfig };
      return this.config;
    }
  }

  /**
   * Save configuration to localStorage
   * @param {Object} config - Configuration to save
   * @param {string} configKey - Key to store config in localStorage
   * @returns {boolean} - Success status
   */
  saveConfig(config = this.config, configKey = 'websiteConfig') {
    try {
      const validationResult = this.validateConfig(config);
      if (!validationResult.isValid) {
        console.error('Configuration validation failed:', validationResult.errors);
        return false;
      }

      localStorage.setItem(configKey, JSON.stringify(config));
      this.config = { ...config };
      this.notifyListeners('configSaved', this.config);
      return true;
    } catch (error) {
      console.error('Error saving configuration:', error);
      return false;
    }
  }

  /**
   * Update specific configuration section
   * @param {string} section - Configuration section to update
   * @param {Object} updates - Updates to apply
   * @returns {boolean} - Success status
   */
  updateConfig(section, updates) {
    try {
      if (!this.config[section]) {
        console.error(`Configuration section '${section}' does not exist`);
        return false;
      }

      this.config[section] = {
        ...this.config[section],
        ...updates
      };

      this.config.website.lastUpdated = new Date().toISOString();
      this.notifyListeners('configUpdated', { section, updates, config: this.config });
      return true;
    } catch (error) {
      console.error('Error updating configuration:', error);
      return false;
    }
  }

  /**
   * Get current configuration
   * @returns {Object} - Current configuration
   */
  getConfig() {
    return { ...this.config };
  }

  /**
   * Get specific configuration section
   * @param {string} section - Section to retrieve
   * @returns {Object|null} - Configuration section or null if not found
   */
  getConfigSection(section) {
    return this.config[section] ? { ...this.config[section] } : null;
  }

  /**
   * Get default configuration
   * @returns {Object} - Default configuration
   */
  getDefaultConfig() {
    return { ...defaultWebsiteConfig };
  }

  /**
   * Reset configuration to default
   * @returns {Object} - Default configuration
   */
  resetToDefault() {
    this.config = { ...defaultWebsiteConfig };
    this.notifyListeners('configReset', this.config);
    return this.config;
  }

  /**
   * Validate configuration against rules
   * @param {Object} config - Configuration to validate
   * @returns {Object} - Validation result with isValid and errors
   */
  validateConfig(config) {
    const errors = [];
    const result = { isValid: true, errors: [] };

    try {
      // Validate website section
      if (config.website) {
        if (!config.website.title || config.website.title.length === 0) {
          errors.push('Website title is required');
        }
        if (config.website.title && config.website.title.length > 100) {
          errors.push('Website title must be less than 100 characters');
        }
        if (!config.website.description || config.website.description.length === 0) {
          errors.push('Website description is required');
        }
        if (!config.website.url || !config.website.url.match(/^https?:\/\/.+/)) {
          errors.push('Valid website URL is required');
        }
      }

      // Validate i18n section
      if (config.i18n) {
        const validLanguages = ['en', 'sc', 'tc', 'th'];
        if (!config.i18n.defaultLanguage || !validLanguages.includes(config.i18n.defaultLanguage)) {
          errors.push('Valid default language is required');
        }
        if (!config.i18n.supportedLanguages || config.i18n.supportedLanguages.length === 0) {
          errors.push('At least one supported language is required');
        }
      }

      // Validate SEO section
      if (config.seo && config.seo.meta) {
        if (!config.seo.meta.keywords || config.seo.meta.keywords.length === 0) {
          errors.push('SEO keywords are required');
        }
      }

      result.isValid = errors.length === 0;
      result.errors = errors;
    } catch (error) {
      result.isValid = false;
      result.errors = ['Configuration validation error: ' + error.message];
    }

    return result;
  }

  /**
   * Merge two configuration objects deeply
   * @param {Object} defaultConfig - Default configuration
   * @param {Object} userConfig - User configuration
   * @returns {Object} - Merged configuration
   */
  mergeConfigs(defaultConfig, userConfig) {
    const merged = { ...defaultConfig };

    for (const key in userConfig) {
      if (userConfig.hasOwnProperty(key)) {
        if (typeof userConfig[key] === 'object' && userConfig[key] !== null && !Array.isArray(userConfig[key])) {
          merged[key] = this.mergeConfigs(merged[key] || {}, userConfig[key]);
        } else {
          merged[key] = userConfig[key];
        }
      }
    }

    return merged;
  }

  /**
   * Export configuration as JSON
   * @param {Object} config - Configuration to export
   * @returns {string} - JSON string
   */
  exportConfig(config = this.config) {
    try {
      return JSON.stringify(config, null, 2);
    } catch (error) {
      console.error('Error exporting configuration:', error);
      return null;
    }
  }

  /**
   * Import configuration from JSON
   * @param {string} jsonString - JSON configuration string
   * @returns {boolean} - Success status
   */
  importConfig(jsonString) {
    try {
      const importedConfig = JSON.parse(jsonString);
      const validationResult = this.validateConfig(importedConfig);
      
      if (!validationResult.isValid) {
        console.error('Imported configuration is invalid:', validationResult.errors);
        return false;
      }

      this.config = this.mergeConfigs(defaultWebsiteConfig, importedConfig);
      this.notifyListeners('configImported', this.config);
      return true;
    } catch (error) {
      console.error('Error importing configuration:', error);
      return false;
    }
  }

  /**
   * Add event listener for configuration changes
   * @param {Function} listener - Listener function
   */
  addListener(listener) {
    if (typeof listener === 'function') {
      this.listeners.push(listener);
    }
  }

  /**
   * Remove event listener
   * @param {Function} listener - Listener function to remove
   */
  removeListener(listener) {
    const index = this.listeners.indexOf(listener);
    if (index > -1) {
      this.listeners.splice(index, 1);
    }
  }

  /**
   * Notify all listeners of configuration changes
   * @param {string} event - Event type
   * @param {Object} data - Event data
   */
  notifyListeners(event, data) {
    this.listeners.forEach(listener => {
      try {
        listener(event, data);
      } catch (error) {
        console.error('Error in configuration listener:', error);
      }
    });
  }

  /**
   * Create a new configuration template for a customer
   * @param {Object} customerInfo - Customer information
   * @returns {Object} - New configuration template
   */
  createCustomerTemplate(customerInfo) {
    const template = { ...defaultWebsiteConfig };
    
    if (customerInfo.companyName) {
      template.website.title = customerInfo.companyName;
      template.seo.openGraph.title = customerInfo.companyName;
      template.seo.openGraph.siteName = customerInfo.companyName;
      template.seo.twitter.title = customerInfo.companyName;
      template.seo.structuredData.name = customerInfo.companyName;
    }

    if (customerInfo.description) {
      template.website.description = customerInfo.description;
      template.website.subtitle = customerInfo.description;
      template.seo.openGraph.description = customerInfo.description;
      template.seo.twitter.description = customerInfo.description;
      template.seo.structuredData.description = customerInfo.description;
    }

    if (customerInfo.website) {
      template.website.url = customerInfo.website;
      template.seo.openGraph.url = customerInfo.website;
      template.seo.structuredData.url = customerInfo.website;
    }

    if (customerInfo.logo) {
      template.website.logo = customerInfo.logo;
      template.seo.structuredData.logo = customerInfo.logo;
    }

    if (customerInfo.primaryColor) {
      template.theme.primaryColor = customerInfo.primaryColor;
    }

    template.website.lastUpdated = new Date().toISOString();
    
    return template;
  }
}

// Create singleton instance
export const configManager = new ConfigManager();

// Utility functions for easy access
export const loadConfig = (configKey) => configManager.loadConfig(configKey);
export const saveConfig = (config, configKey) => configManager.saveConfig(config, configKey);
export const getConfig = () => configManager.getConfig();
export const updateConfig = (section, updates) => configManager.updateConfig(section, updates);
export const validateConfig = (config) => configManager.validateConfig(config);
export const createCustomerTemplate = (customerInfo) => configManager.createCustomerTemplate(customerInfo);

export default configManager;